import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { MobileMenuService } from './mobile-menu.service';

/**
 * Sole owner of in-page CTA navigation. Every entry point (hero, navbar,
 * sticky CTA, announcement bar, in-prose anchors) funnels through here so
 * the scroll sequence — close overlay → wait for layout → measure → scroll —
 * is authored once.
 *
 * Router `anchorScrolling` is disabled in app.config.ts so Angular does not
 * fire a second scroll against scroll-margin-top that races with ours.
 */
@Injectable({ providedIn: 'root' })
export class NavigationService {
  private router = inject(Router);
  private menu = inject(MobileMenuService);

  private readonly formId = 'get-a-call';

  /**
   * Scroll to the lead-form section. Works from any route and from any CTA
   * (including the hamburger overlay, which is closed en route). Measures the
   * target after layout has fully settled so the scroll math is not racing
   * overlay close, route mount, or late-loading hero media.
   */
  async goToQuoteForm(): Promise<void> {
    // 1. If on a different route, navigate home first. router.navigate resolves
    //    after NavigationEnd has already fired — no need to await it separately.
    if (this.router.url !== '/' && !this.router.url.startsWith('/#')) {
      await this.router.navigate(['/'], { fragment: this.formId });
    }

    // 2. If the overlay is open, close it and wait for the transition end.
    //    waitUntilClosed resolves immediately if already closed.
    await this.menu.waitUntilClosed();

    // 3. Wait for the lazy-loaded home component to mount — the #get-a-call
    //    section may not exist yet when NavigationEnd fires.
    const section = await this.waitForElement(this.formId);
    if (!section) return;

    // 4. Wait for the section's position to stabilize. Hero video + below-fold
    //    images shift layout for a few frames after mount; scrolling to a
    //    moving target lands the title in the wrong place.
    await this.waitForStableRect(section);

    this.scrollToForm(section);

    // 5. Correct for layout shifts that happen DURING the smooth-scroll
    //    animation (lazy-loaded images above the form can push it down while
    //    the browser is animating toward a stale target). Re-measure after
    //    the animation has settled, then nudge if the landing drifted.
    await this.correctScrollAfterLayoutShift(section);
  }

  /**
   * Navigate to the top of the home page. If the current URL already is /
   * (possibly with a stale #get-a-call fragment from a prior CTA), force the
   * fragment to clear and scroll to the top — never leave the user stranded
   * at the form.
   */
  async goHome(): Promise<void> {
    await this.menu.waitUntilClosed();
    // replaceUrl + no fragment clears a lingering #get-a-call from the URL.
    await this.router.navigate(['/'], { fragment: undefined, replaceUrl: true });
    await this.nextPaint();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  /**
   * Intercept anchor clicks inside innerHTML containers (blog prose, clinic
   * content) so internal CTAs route through the unified scroll logic.
   * Usage: (click)="nav.handleProseClick($event)"
   */
  handleProseClick(event: Event): void {
    const target = event.target as HTMLElement;
    const anchor = target.closest('a');
    if (!anchor) return;

    const href = anchor.getAttribute('href');
    if (href === '/get-a-quote' || href === '/#get-a-call') {
      event.preventDefault();
      void this.goToQuoteForm();
    }
  }

  private scrollToForm(preMeasured?: HTMLElement): void {
    const section = preMeasured ?? document.getElementById(this.formId);
    if (!section) return;

    const nav = document.querySelector<HTMLElement>('.navbar');
    const navHeight = nav ? Math.round(nav.getBoundingClientRect().height) : 56;
    const gap = this.readLandGap();

    // Land the *title* (not the section box) at navHeight + gap from the
    // viewport top. Lands independent of the section's own padding-block so
    // the reading-rhythm padding does not become dead space post-scroll.
    const landing = section.querySelector<HTMLElement>('h1, h2, h3') ?? section;
    const targetY =
      landing.getBoundingClientRect().top + window.scrollY - navHeight - gap;

    window.scrollTo({ top: Math.max(0, targetY), behavior: 'smooth' });
  }

  private readLandGap(): number {
    const raw = getComputedStyle(document.documentElement)
      .getPropertyValue('--scroll-land-gap')
      .trim();
    const parsed = parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : 24;
  }

  private nextPaint(): Promise<void> {
    // rAF with a setTimeout fallback so the loop still advances when the tab
    // is backgrounded (Chrome throttles rAF to once-per-minute on hidden tabs,
    // which otherwise hangs the service indefinitely).
    return new Promise(resolve => {
      let settled = false;
      const finish = () => { if (!settled) { settled = true; resolve(); } };
      requestAnimationFrame(finish);
      setTimeout(finish, 100);
    });
  }

  /**
   * Wait for an element with the given id to appear in the DOM. Resolves with
   * the element, or null after the timeout. Needed for lazy-loaded routes —
   * Angular's NavigationEnd fires before the route's component tree mounts,
   * so measuring right after nav can return null.
   */
  private waitForElement(id: string, timeoutMs = 2000): Promise<HTMLElement | null> {
    const existing = document.getElementById(id);
    if (existing) return Promise.resolve(existing);

    return new Promise(resolve => {
      const obs = new MutationObserver(() => {
        const el = document.getElementById(id);
        if (el) { obs.disconnect(); clearTimeout(timer); resolve(el); }
      });
      obs.observe(document.body, { childList: true, subtree: true });
      const timer = setTimeout(() => { obs.disconnect(); resolve(null); }, timeoutMs);
    });
  }

  /**
   * Wait for an element's position to stop changing — i.e. layout has settled.
   * Checks `top` over successive animation frames; returns once the rect has
   * been stable (< 0.5px drift) for `framesStable` consecutive frames, or
   * when `maxFrames` is reached. Guards against scrolling to a target that
   * shifts mid-animation because late images or the hero video reflow.
   */
  /**
   * After the smooth scroll has been kicked off, wait for scrollY to settle
   * (animation complete) and then verify the landing is still correct. If
   * the section drifted (usually because a lazy image above the form loaded
   * mid-animation and pushed it down), fire a second instant scroll to snap
   * the title exactly where it belongs.
   *
   * Bounded — gives up after ~2.5s of scroll instability so it never blocks
   * a pathological page indefinitely.
   */
  private async correctScrollAfterLayoutShift(section: HTMLElement): Promise<void> {
    // Wait for scrollY to stop changing (smooth scroll complete OR layout still
    // dragging). Check every 80ms for up to 2.5s.
    let lastY = -1;
    for (let i = 0; i < 32; i++) {
      await new Promise(r => setTimeout(r, 80));
      const y = window.scrollY;
      if (Math.abs(y - lastY) < 0.5) break;
      lastY = y;
    }

    const nav = document.querySelector<HTMLElement>('.navbar');
    const navHeight = nav ? Math.round(nav.getBoundingClientRect().height) : 56;
    const gap = this.readLandGap();
    const landing = section.querySelector<HTMLElement>('h1, h2, h3') ?? section;
    const currentTitleTop = landing.getBoundingClientRect().top;
    const desiredTitleTop = navHeight + gap;
    const drift = currentTitleTop - desiredTitleTop;

    // If drift is more than 2px, correct with an instant scroll. Smooth would
    // re-introduce the same race we just resolved.
    if (Math.abs(drift) > 2) {
      window.scrollTo({ top: Math.max(0, window.scrollY + drift), behavior: 'instant' as ScrollBehavior });
    }
  }

  private async waitForStableRect(
    el: Element,
    framesStable = 3,
    maxFrames = 60,
  ): Promise<void> {
    let last = el.getBoundingClientRect().top;
    let stableCount = 0;
    for (let i = 0; i < maxFrames; i++) {
      await this.nextPaint();
      const curr = el.getBoundingClientRect().top;
      if (Math.abs(curr - last) < 0.5) {
        stableCount++;
        if (stableCount >= framesStable) return;
      } else {
        stableCount = 0;
      }
      last = curr;
    }
  }
}
